import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  IconButton,
  Slide,
  Typography,
  useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUserData } from "../../features/auth/authSlice";
import { useFirstLoginMutation } from "../../features/users/usersApiSlice";
import { useGetTenantPlanQuery } from "../../features/tenants/tenantsApiSlice";
import { startMemberTour } from "../../features/tour/tourSlice";
import {
  hasFeatureAccess,
  normalizePlanId,
  FEATURE_KEYS,
  PLAN_IDS,
} from "../../utils/planFeatures";

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

// Descriptions aligned with MemberHomePage TILES
const featureCards = [
  {
    icon: "mdi:account-circle-outline",
    title: "Meine Daten",
    description: "Persönliche Angaben und Vertrag einsehen",
    color: "#1976d2",
    featureKey: FEATURE_KEYS.MEMBER_PORTAL,
  },
  {
    icon: "fa-solid:torii-gate",
    title: "Standorte",
    description: "Alle Standortinfos und Detailansicht auf einen Blick",
    color: "#00695c",
    featureKey: FEATURE_KEYS.MEMBER_PORTAL,
  },
  {
    icon: "mdi:cash-check",
    title: "Prüfungsgebühren",
    description: "Offene und bezahlte Gebühren im Blick",
    color: "#2e7d32",
    featureKey: FEATURE_KEYS.MEMBER_PORTAL,
  },
  {
    icon: "mdi:newspaper-variant-outline",
    title: "Neuigkeiten",
    description: "Aktuelle Nachrichten aus dem Verein",
    color: "#7b1fa2",
    featureKey: FEATURE_KEYS.NEWS,
  },
  {
    icon: "fa-solid:yin-yang",
    title: "E-Dojang",
    description: "Online-Lernen mit Lehrvideos und Prüfungsprogramm",
    color: "#8d6e63",
    featureKey: FEATURE_KEYS.E_DOJANG,
  },
  {
    icon: "majesticons:analytics-plus",
    title: "Aufgaben",
    description: "Ihre offenen Aufgaben und To-dos",
    color: "#ed6c02",
    featureKey: FEATURE_KEYS.TASKS,
  },
  {
    icon: "fa-solid:calendar-check",
    title: "Veranstaltungen",
    description: "Termine und Veranstaltungen Ihres Vereins",
    color: "#0288d1",
    featureKey: FEATURE_KEYS.EVENTS,
  },
  {
    icon: "game-icons:shop",
    title: "Shop",
    description: "Produkte und Bestellungen",
    color: "#c62828",
    featureKey: FEATURE_KEYS.SHOP,
  },
  {
    icon: "wpf:survey",
    title: "Befragungen",
    description: "An Umfragen teilnehmen",
    color: "#00838f",
    featureKey: FEATURE_KEYS.SURVEY,
  },
  {
    icon: "mdi:lifebuoy",
    title: "Hilfe & Support",
    description: "Bei Fragen oder Problemen",
    color: "#f57c00",
    featureKey: null,
  },
];

const TOTAL_STEPS = 3;

/**
 * Formal greeting based on title (Herr/Frau) from user data.
 * Falls back to firstName, then generic greeting.
 */
const buildGreeting = (userData) => {
  const title = (userData?.title || "").trim();
  const firstName = (userData?.firstName || "").trim();
  const lastName = (userData?.lastName || "").trim();

  if (title && lastName) {
    return `Willkommen, ${title} ${lastName}!`;
  }
  if (firstName) {
    return `Willkommen, ${firstName}!`;
  }
  return "Herzlich willkommen!";
};

const MemberWelcomeDialog = ({ handleClose }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const userData = useSelector(selectCurrentUserData);
  const [activeStep, setActiveStep] = useState(0);
  const [firstLogin] = useFirstLoginMutation();

  const { data: tenant } = useGetTenantPlanQuery();
  const planId = normalizePlanId(tenant?.billingPlan || PLAN_IDS.BASIS);

  const visibleFeatures = featureCards.filter(
    (card) => !card.featureKey || hasFeatureAccess(planId, card.featureKey),
  );

  const handleNext = () => setActiveStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleDontShowAgain = async () => {
    try {
      const result = await firstLogin().unwrap();
      if (result?.status === "SUCCESS") {
        handleClose();
      } else {
        window.alert(
          "Fehler bei der Kommunikation mit dem Server, bitte versuchen Sie es erneut.",
        );
      }
    } catch (error) {
      console.log("error :>> ", error);
      window.alert(
        "Fehler bei der Kommunikation mit dem Server, bitte versuchen Sie es erneut.",
      );
    }
  };

  const handleStartTour = () => {
    dispatch(startMemberTour());
    handleClose();
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: `${theme.palette.primary.main}14`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2.5,
              }}
            >
              <Icon
                icon="mdi:hand-wave"
                width={40}
                style={{ color: theme.palette.primary.main }}
              />
            </Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {buildGreeting(userData)}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 440, mx: "auto", mb: 2 }}
            >
              Schön, dass Sie da sind! Dies ist Ihr persönlicher Bereich für
              alles rund um Ihre Mitgliedschaft.
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 440, mx: "auto" }}
            >
              Keine Sorge — Sie können hier nichts kaputt machen. Schauen Sie
              sich in Ruhe um.
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 1 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              textAlign="center"
              gutterBottom
            >
              Ihre Möglichkeiten
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 2 }}
            >
              Diese Bereiche stehen Ihnen zur Verfügung:
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                maxHeight: 340,
                overflow: "auto",
              }}
            >
              {visibleFeatures.map((card) => (
                <Card key={card.title} variant="outlined" sx={{ flexShrink: 0 }}>
                  <CardContent
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      py: 1.5,
                      px: 2,
                      "&:last-child": { pb: 1.5 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        bgcolor: `${card.color}14`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mr: 2,
                      }}
                    >
                      <Icon
                        icon={card.icon}
                        width={24}
                        style={{ color: card.color }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {card.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {card.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: `${theme.palette.primary.main}14`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2.5,
              }}
            >
              <Icon
                icon="mdi:compass-outline"
                width={40}
                style={{ color: theme.palette.primary.main }}
              />
            </Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Bereit? Los geht's!
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 440, mx: "auto", mb: 1 }}
            >
              Wir zeigen Ihnen Schritt für Schritt, wo Sie alles finden.
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 440, mx: "auto", mb: 3 }}
            >
              Die Tour dauert nur einen kurzen Moment und Sie können sie
              jederzeit abbrechen.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleStartTour}
              startIcon={<Icon icon="mdi:compass-outline" />}
              sx={{
                px: 4,
                py: 1.2,
                boxShadow: "0 10px 28px rgba(25, 118, 210, 0.28)",
                animation: "memberTourPulse 2.6s ease-in-out infinite",
                "@keyframes memberTourPulse": {
                  "0%": {
                    transform: "translateY(0)",
                    boxShadow: "0 10px 28px rgba(25, 118, 210, 0.24)",
                  },
                  "50%": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 14px 36px rgba(25, 118, 210, 0.38)",
                  },
                  "100%": {
                    transform: "translateY(0)",
                    boxShadow: "0 10px 28px rgba(25, 118, 210, 0.24)",
                  },
                },
              }}
            >
              Tour starten
            </Button>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="text"
                color="inherit"
                size="small"
                onClick={handleClose}
                sx={{ color: "text.secondary" }}
              >
                Nein danke, ich schaue mich selbst um
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      TransitionComponent={Transition}
      open
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      {/* Header with step indicator and close */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pt: 1.5,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Schritt {activeStep + 1} von {TOTAL_STEPS}
        </Typography>

        {/* Step dots */}
        <Box sx={{ display: "flex", gap: 0.75 }}>
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <Box
              key={i}
              sx={{
                width: i === activeStep ? 20 : 8,
                height: 8,
                borderRadius: 4,
                bgcolor:
                  i === activeStep
                    ? "primary.main"
                    : i < activeStep
                      ? "primary.light"
                      : "grey.300",
                transition: "all 0.3s ease",
              }}
            />
          ))}
        </Box>

        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="Dialog schließen"
          sx={{ color: "text.secondary" }}
        >
          <Icon icon="mdi:close" width={20} />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 1 }}>
        {renderStep()}
      </DialogContent>

      {/* Navigation footer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pb: 2,
          pt: 1,
        }}
      >
        {/* Left side: Back button or "Don't show again" */}
        <Box>
          {activeStep === 0 ? (
            <Button
              onClick={handleDontShowAgain}
              variant="text"
              size="small"
              sx={{ color: "text.secondary", textTransform: "none" }}
            >
              Nicht wieder anzeigen
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={handleBack}
              startIcon={<Icon icon="mdi:arrow-left" />}
            >
              Zurück
            </Button>
          )}
        </Box>

        {/* Right side: Next or Close */}
        <Box>
          {activeStep < TOTAL_STEPS - 1 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              endIcon={<Icon icon="mdi:arrow-right" />}
            >
              Weiter
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleClose}
              size="small"
            >
              Schließen
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default MemberWelcomeDialog;
